import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IGroupOverview, useGroupRoster, useStaffUsers } from 'core/hooks';

import { PRIORITIES } from './taskBuckets';
import * as S from './TaskBoard.styled';

export interface ICreateTaskDraft {
  title: string;
  groupId?: string;
  studentId?: string;
  agreementId?: string;
  assigneeId?: string;
  dueDate?: string;
  priority: string;
}

interface ITaskCreateFormProps {
  /** Fixed group scope (group tab) — hides the group picker. */
  groupId?: string;
  /** Fixed student scope (student tab) — hides group + roster pickers. */
  studentId?: string;
  /** Group list for the dashboard's create picker. */
  groups?: IGroupOverview[];
  adding: boolean;
  onCreate: (draft: ICreateTaskDraft) => Promise<boolean>;
}

/**
 * Inline task-create form (title / group / roster / due / priority /
 * assignee). Owns its own draft state; resets on successful create.
 */
export const TaskCreateForm = memo(
  ({ groupId, studentId, groups, adding, onCreate }: ITaskCreateFormProps) => {
    const { t } = useTranslation();

    const isDashboard = !groupId && !studentId;

    const [title, setTitle] = useState('');
    const [formGroupId, setFormGroupId] = useState('');
    const [rosterRowId, setRosterRowId] = useState('');
    const [formDue, setFormDue] = useState('');
    const [priority, setPriority] = useState('normal');
    // '' = default assignee (the current user, applied by the backend).
    const [formAssigneeId, setFormAssigneeId] = useState('');

    const { staffUsers } = useStaffUsers(false);

    // Group whose roster feeds the create picker: the scoped group, or the
    // group the user picks in the dashboard create form.
    const rosterGroupId = groupId || formGroupId;
    const { rows: roster } = useGroupRoster(rosterGroupId || undefined);
    const pickedRow = roster.find((r) => r?.id === rosterRowId);

    // In a student tab the form has no group/roster — it creates a
    // student-scoped task. Otherwise a group must be chosen (fixed or picked).
    const createGroupId = groupId || formGroupId;
    const canCreate =
      Boolean(title.trim()) && (Boolean(studentId) || Boolean(createGroupId));

    const submit = useCallback(async () => {
      if (!title.trim() || (!studentId && !createGroupId)) return;
      const ok = await onCreate({
        title: title.trim(),
        groupId: createGroupId || undefined,
        studentId: studentId || pickedRow?.student?.id || undefined,
        agreementId: pickedRow?.id || undefined,
        assigneeId: formAssigneeId || undefined,
        dueDate: formDue || undefined,
        priority,
      });
      if (ok) {
        setTitle('');
        setFormDue('');
        setRosterRowId('');
        setFormAssigneeId('');
      }
    }, [
      onCreate,
      title,
      studentId,
      createGroupId,
      pickedRow,
      formAssigneeId,
      formDue,
      priority,
    ]);

    const rosterLabel = (row: typeof roster[number]): string => {
      const name =
        [row?.student?.user?.lastName, row?.student?.user?.firstName]
          .filter(Boolean)
          .join(' ') || '—';
      return row?.agreementTitle ? `${name} · ${row.agreementTitle}` : name;
    };

    return (
      <S.TaskForm>
        <S.TitleInput
          value={title}
          placeholder={t('pages.dashboard.taskTitlePlaceholder')}
          onChange={(e) => setTitle(e.target.value)}
        />
        {isDashboard ? (
          <S.Select
            value={formGroupId}
            onChange={(e) => {
              setFormGroupId(e.target.value);
              setRosterRowId('');
            }}
          >
            <option value="">{t('pages.dashboard.pickGroup')}</option>
            {(groups || []).map((g) => (
              <option key={g.groupId} value={g.groupId}>
                {g.name}
              </option>
            ))}
          </S.Select>
        ) : null}
        {!studentId && rosterGroupId ? (
          <S.Select
            value={rosterRowId}
            onChange={(e) => setRosterRowId(e.target.value)}
          >
            <option value="">{t('pages.dashboard.wholeGroup')}</option>
            {roster.map((r) => (
              <option key={r?.id} value={r?.id}>
                {rosterLabel(r)}
              </option>
            ))}
          </S.Select>
        ) : null}
        <S.Input
          type="date"
          value={formDue}
          onChange={(e) => setFormDue(e.target.value)}
        />
        <S.Select
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
        >
          {PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {t(`pages.tasks.priority.${p}`)}
            </option>
          ))}
        </S.Select>
        <S.Select
          value={formAssigneeId}
          title={t('pages.tasks.assignee')}
          onChange={(e) => setFormAssigneeId(e.target.value)}
        >
          <option value="">{t('pages.tasks.assigneeMe')}</option>
          {staffUsers.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name || u.email}
            </option>
          ))}
        </S.Select>
        <S.AddButton
          type="button"
          disabled={adding || !canCreate}
          onClick={submit}
        >
          {t('pages.tasks.add')}
        </S.AddButton>
      </S.TaskForm>
    );
  },
);

TaskCreateForm.displayName = 'TaskCreateForm';
